import JSZip from 'jszip';
import Papa from 'papaparse';
import { readFileContent } from '../ImportService';
import { EMPTY_FORM_DATA } from '../DataService';
import { v4 as uuidv4 } from 'uuid';

// --- Expected CSV Filenames (Adjust if necessary based on actual LinkedIn exports) ---
const CSV_FILES = {
  PROFILE: 'Profile.csv',
  POSITIONS: 'Positions.csv',
  EDUCATION: 'Education.csv',
  SKILLS: 'Skills.csv',
  LANGUAGES: 'Languages.csv',
  // Add other relevant files as needed (e.g., Certifications.csv, Projects.csv)
};

// --- Helper Functions ---

/**
 * Parses a date string from LinkedIn (e.g., "Jan 2020", "Present", "2019")
 * into a simplified format (e.g., "2020-01", "Present", "2019").
 * Returns null if parsing fails.
 * @param {string} dateString
 * @returns {string | null}
 */
const parseLinkedInDate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return null;
  dateString = dateString.trim();

  if (dateString.toLowerCase() === 'present') {
    return 'Present';
  }

  // Check for numeric year first (e.g., "2019")
  if (/^\d{4}$/.test(dateString)) {
    return dateString;
  }

  // Try parsing "Mon YYYY" format
  const dateParts = dateString.split(' ');
  if (dateParts.length === 2) {
    const monthStr = dateParts[0];
    const yearStr = dateParts[1];
    const monthMap = { Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06', Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12' };

    if (monthMap[monthStr] && /^\d{4}$/.test(yearStr)) {
      return `${yearStr}-${monthMap[monthStr]}`;
    }
  }

  console.warn(`Could not parse LinkedIn date: "${dateString}"`);
  return dateString; // Return original string as fallback
};

/**
 * Maps parsed LinkedIn CSV data to the application's formData structure.
 * @param {object} parsedCsvData - An object where keys are CSV filenames and values are parsed CSV data (array of objects).
 * @returns {{formData: object, populatedSectionKeys: string[]}}
 */
const mapLinkedInDataToFormData = (parsedCsvData) => {
  // Create a deep copy to avoid modifying the template object
  const formData = JSON.parse(JSON.stringify(EMPTY_FORM_DATA));
  const populatedSectionKeys = [];

  try {
    // 1. Map Profile Data
    const profileData = parsedCsvData[CSV_FILES.PROFILE]?.[0]; // Profile is usually a single row
    if (profileData) {
      formData.personalInfo.firstName = profileData['First Name'] || '';
      formData.personalInfo.lastName = profileData['Last Name'] || '';
      formData.personalInfo.professionalTitle = profileData['Headline'] || '';
      // Address/Location might be split or combined; adjust as needed
      formData.personalInfo.address = profileData['Location'] || '';
      formData.professionalSummary = profileData['Summary'] || '';

      // Check if personalInfo or summary was actually populated
      if (Object.values(formData.personalInfo).some(val => val))
          populatedSectionKeys.push('personalInfo');
      if (formData.professionalSummary)
          populatedSectionKeys.push('professionalSummary');
    }

    // 2. Map Positions (Work Experience)
    const positionsData = parsedCsvData[CSV_FILES.POSITIONS];
    if (Array.isArray(positionsData) && positionsData.length > 0) {
      formData.workExperiences = positionsData.map(pos => ({
        id: uuidv4(),
        companyName: pos['Company Name'] || '',
        jobTitle: pos['Title'] || '',
        location: pos['Location'] || '',
        // Dates need careful parsing
        startDate: parseLinkedInDate(pos['Started On']), // Assuming column name
        endDate: parseLinkedInDate(pos['Finished On']),   // Assuming column name
        description: pos['Description'] || '',
        expanded: true,
      }));
      if (formData.workExperiences.length > 0) populatedSectionKeys.push('workExperiences');
    }

    // 3. Map Education
    const educationData = parsedCsvData[CSV_FILES.EDUCATION];
    if (Array.isArray(educationData) && educationData.length > 0) {
      formData.educations = educationData.map(edu => ({
        id: uuidv4(),
        schoolName: edu['School Name'] || '',
        degree: edu['Degree Name'] || '', // Or 'Degree'?
        fieldOfStudy: edu['Field Of Study'] || '',
        startDate: parseLinkedInDate(edu['Start Date']), // Assuming column name
        endDate: parseLinkedInDate(edu['End Date']),     // Assuming column name
        description: edu['Notes'] || '', // Or 'Activities and Societies'?
        expanded: true,
      }));
      if (formData.educations.length > 0) populatedSectionKeys.push('educations');
    }

    // 4. Map Skills
    const skillsData = parsedCsvData[CSV_FILES.SKILLS];
    if (Array.isArray(skillsData) && skillsData.length > 0) {
      // Assuming your skills section stores an array of skill objects { id, name, level? }
      // LinkedIn export might just be a list of names.
      formData.skills = skillsData.map(skill => ({
        id: uuidv4(),
        name: skill['Name'] || '', // Assuming column name
        // level: 'Intermediate' // Default or leave empty
      }));
      if (formData.skills.length > 0) populatedSectionKeys.push('skills');
    }

    // 5. Map Languages
    const languagesData = parsedCsvData[CSV_FILES.LANGUAGES];
    if (Array.isArray(languagesData) && languagesData.length > 0) {
      formData.languages = languagesData.map(lang => ({
        id: uuidv4(),
        language: lang['Name'] || '', // Assuming column name
        proficiency: lang['Proficiency'] || '', // Assuming column name
      }));
      if (formData.languages.length > 0) populatedSectionKeys.push('languages');
    }

    // --- Add Mappings for other sections (Certifications, Projects, etc.) here if needed ---

  } catch (error) {
    console.error("Error mapping LinkedIn data:", error);
    // Return partially mapped data or throw?
    // For now, return what we have, but log the error.
  }

  return { formData, populatedSectionKeys: [...new Set(populatedSectionKeys)] }; // Ensure unique keys
};

/**
 * Parses a LinkedIn export ZIP file.
 * @param {File} file - The ZIP file object.
 * @returns {Promise<{formData: object, populatedSectionKeys: string[]}>} A promise resolving with the parsed data structure.
 */
export const parseLinkedInZip = async (file) => {
  console.log('Starting LinkedIn ZIP parsing...');
  let zip;
  try {
    const fileContent = await readFileContent(file, 'arrayBuffer');
    console.log('Read file content (ArrayBuffer).');
    zip = await JSZip.loadAsync(fileContent);
    console.log('ZIP file loaded.');
  } catch (error) {
    console.error('Error reading or loading ZIP file:', error);
    throw new Error(`Failed to read or load ZIP file: ${error.message}`);
  }

  const parsedCsvData = {};
  const expectedFiles = Object.values(CSV_FILES);
  let filesFoundCount = 0;

  // Iterate through expected files and parse them
  for (const filename of expectedFiles) {
    const fileEntry = zip.file(filename);
    if (fileEntry) {
      try {
        console.log(`Found and parsing: ${filename}`);
        const csvText = await fileEntry.async('string');
        const parseResult = Papa.parse(csvText, {
          header: true,       // Use first row as header keys
          skipEmptyLines: true,
          transformHeader: header => header.trim(), // Trim header spaces
        });

        if (parseResult.errors.length > 0) {
          console.warn(`CSV parsing errors in ${filename}:`, parseResult.errors);
          // Decide whether to continue or throw based on errors
        }

        parsedCsvData[filename] = parseResult.data;
        filesFoundCount++;
        console.log(`Successfully parsed ${filename}`);
      } catch (error) {
        console.error(`Error parsing CSV file ${filename}:`, error);
        // Optionally throw an error or just skip this file
        throw new Error(`Failed to parse ${filename} from ZIP: ${error.message}`);
      }
    } else {
      console.log(`Expected file not found in ZIP: ${filename}`);
      // Make this optional - not all exports contain all files
      // throw new Error(`Required file ${filename} not found in ZIP archive.`);
    }
  }

  if (filesFoundCount === 0) {
      throw new Error('No relevant CSV files (Profile, Positions, etc.) found in the provided ZIP archive.');
  }

  console.log('Mapping parsed CSV data to formData...');
  const result = mapLinkedInDataToFormData(parsedCsvData);
  console.log('LinkedIn parsing and mapping complete.');
  return result;
}; 